import { DISCOVERY_API_URL } from '@constants/externalRoutes';
import { AxiosResponse } from 'axios';
import api from '../utils/api';

export interface Cluster {
  id: string;
  name: string;
  description: string;
  image: string;
  images: { type: string; url: string }[];
  isFavorite: boolean;
}

export interface ClusterSuggestion {
  cluster: Cluster;
  raw_aptitude_fit: number;
  raw_interest_fit: number;
}

const APTITUDE_ORDER = 'aptitude';
const INTEREST_ORDER = 'interest';

const orderClusters = (clustersSuggested: ClusterSuggestion[], order: 'aptitude' | 'interest') =>
  clustersSuggested.sort((a: ClusterSuggestion, b: ClusterSuggestion) => {
    let orderFilter = order;

    if (!a[`raw_${orderFilter}_fit`]) {
      orderFilter = orderFilter === APTITUDE_ORDER ? INTEREST_ORDER : APTITUDE_ORDER;
    }

    const greaterNumber = orderFilter === APTITUDE_ORDER ? 1 : -1;
    const lowerNumber = orderFilter === APTITUDE_ORDER ? -1 : 1;

    const aFit = a[`raw_${orderFilter}_fit`];
    const bFit = b[`raw_${orderFilter}_fit`];

    const aHasHigherFit = aFit > bFit;
    const equalFitAndAHasLowerName = aFit === bFit && a.cluster.name < b.cluster.name;

    return aHasHigherFit || equalFitAndAHasLowerName ? greaterNumber : lowerNumber;
  });

const getClustersSaved = async (userId: string): Promise<Cluster[]> => {
  const response: AxiosResponse<{
    data: {
      favorites: ClusterSuggestion[];
      suggested: ClusterSuggestion[];
    };
  }> = await api(`${DISCOVERY_API_URL}/users/${userId}/favorite_clusters`);

  const clusters = [...response?.data?.data?.favorites, ...response?.data?.data?.suggested];

  const orderedClusters: Cluster[] = orderClusters(clusters, 'aptitude').map((x: ClusterSuggestion) => ({
    ...x.cluster,
    isFavorite: response?.data?.data?.favorites.some(
      (favorite: ClusterSuggestion) => favorite.cluster.id === x.cluster.id,
    ),
  }));

  return orderedClusters;
};

const saveFavoriteCluster = async (clusterId: string, userId: string): Promise<Cluster> => {
  const response: AxiosResponse<Cluster> = await api(`${DISCOVERY_API_URL}/users/${userId}/favorite_clusters`, {
    method: 'POST',
    data: {
      cluster: {
        cluster_id: clusterId,
        user_id: userId,
      },
    },
  });

  return response?.data;
};

const unsaveFavoriteCluster = async (clusterId: string, userId: string): Promise<Cluster> => {
  const response: AxiosResponse<Cluster> = await api(
    `${DISCOVERY_API_URL}/users/${userId}/favorite_clusters/${clusterId}`,
    {
      method: 'DELETE',
      data: {
        cluster: {
          cluster_id: clusterId,
          user_id: userId,
        },
      },
    },
  );

  return response?.data;
};

const ClusterService = { getClustersSaved, saveFavoriteCluster, unsaveFavoriteCluster };

export default ClusterService;
